import { Edit, Eye, FileText, Home, Plus, Trash2, Upload } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import ContractUpload from '../../components/ContractUpload/ContractUpload';
import api from '../../services/api';
import ContractModal from '../Contracts/ContractModal';

const RMCContracts = () => {
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const queryClient = useQueryClient();

  // 사용자 정보 가져오기
  const { data: userInfo } = useQuery(['user-info'], () =>
    api.get('/auth/me'),
    {
      onSuccess: (data) => {
        setCurrentUser(data);
      }
    }
  );

  // RMC 사용자 여부 확인
  const isRMCUser = userInfo?.role === 'rental_management_company' || 
                    userInfo?.role === 'rmc_admin' || 
                    userInfo?.role === 'rmc_staff';

  // 임대사업자 목록 가져오기 (RMC 사용자용)
  const { data: propertyOwners } = useQuery(
    ['property-owners', userInfo?.role],
    () => {
      // RMC 사용자는 RMC 전용 API 사용
      if (isRMCUser) {
        return api.get('/rmc/property-owners');
      }
      return api.get('/property-owners');
    },
    {
      enabled: isRMCUser,
      staleTime: 5 * 60 * 1000, // 5분
      cacheTime: 10 * 60 * 1000, // 10분
      select: (data) => {
        // API 응답이 배열인지 확인하고, 아니면 빈 배열 반환
        if (Array.isArray(data)) {
          return data;
        }
        // 에러 응답이나 다른 구조인 경우 빈 배열 반환
        console.warn('propertyOwners API 응답이 배열이 아닙니다:', data);
        return [];
      }
    }
  );

  // 건물 목록 가져오기 (RMC 사용자는 임대사업자별, 임대사업자는 자신의 건물)
  const { data: buildings } = useQuery(
    ['buildings', selectedPropertyOwner, userInfo?.role],
    () => {
      if (isRMCUser) {
        // RMC 사용자는 선택된 임대사업자의 건물
        const url = selectedPropertyOwner
          ? `/buildings?property_owner_id=${selectedPropertyOwner}`
          : '/buildings';
        return api.get(url);
      } else {
        // 임대사업자 사용자는 자신의 건물
        return api.get('/buildings');
      }
    },
    {
      enabled: !!userInfo && (!isRMCUser || !!selectedPropertyOwner),
      select: (data) => {
        // API 응답이 배열인지 확인하고, 아니면 빈 배열 반환
        if (Array.isArray(data)) {
          return data;
        }
        // 에러 응답이나 다른 구조인 경우 빈 배열 반환
        console.warn('buildings API 응답이 배열이 아닙니다:', data);
        return [];
      }
    }
  );

  // 계약 목록 가져오기
  const { data: contractsData, isLoading, error } = useQuery(
    ['contracts', selectedPropertyOwner, selectedBuilding, statusFilter],
    () => {
      const params = new URLSearchParams();
      if (selectedPropertyOwner) {
        params.append('property_owner_id', selectedPropertyOwner);
      }
      if (selectedBuilding) {
        params.append('building_id', selectedBuilding);
      }
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      const url = params.toString() ? `/contracts?${params.toString()}` : '/contracts';
      return api.get(url);
    },
    {
      enabled: !!userInfo && !!selectedBuilding,
      onSuccess: (data) => {
        console.log('🔍 계약 목록 조회 성공:', data);
      },
      onError: (error) => {
        console.error('계약 목록 조회 실패:', error);
      }
    }
  );

  // contracts 데이터 안전하게 처리
  const contracts = Array.isArray(contractsData) ? contractsData : 
                   Array.isArray(contractsData?.data) ? contractsData.data : 
                   Array.isArray(contractsData?.contracts) ? contractsData.contracts : [];

  useEffect(() => {
    if (!isRMCUser || selectedPropertyOwner || !Array.isArray(propertyOwners) || propertyOwners.length === 0) {
      return;
    }

    setSelectedPropertyOwner(String(propertyOwners[0].id));
  }, [isRMCUser, propertyOwners, selectedPropertyOwner]);

  useEffect(() => {
    if (!Array.isArray(buildings) || buildings.length === 0) {
      return;
    }

    const hasSelectedBuilding = buildings.some((building) => String(building.id) === String(selectedBuilding));
    if (!hasSelectedBuilding) {
      setSelectedBuilding(String(buildings[0].id));
    }
  }, [buildings, selectedBuilding]);

  // 임대사업자 변경 핸들러
  const handlePropertyOwnerChange = (propertyOwnerId) => {
    setSelectedPropertyOwner(propertyOwnerId);
    setSelectedBuilding(''); // 건물 선택 초기화
  };

  // 계약 삭제
  const deleteContractMutation = useMutation(
    (contractId) => api.delete(`/contracts/${contractId}`),
    {
      onSuccess: () => {
        toast.success('계약이 성공적으로 삭제되었습니다.');
        queryClient.invalidateQueries('contracts');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '계약 삭제에 실패했습니다.');
      }
    }
  );

  const handleDeleteContract = (contractId) => {
    if (window.confirm('정말로 이 계약을 삭제하시겠습니까?')) {
      deleteContractMutation.mutate(contractId);
    }
  };

  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEditContract = (contract) => {
    setSelectedContract(contract);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleAddContract = () => {
    setSelectedContract(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleUploadClick = () => {
    console.log('🔍 엑셀 업로드 버튼 클릭');
    console.log('🔍 showUploadModal 현재 값:', showUploadModal);
    // 건물 선택은 모달에서 할 수 있도록 건물 선택 체크 제거
    setShowUploadModal(true);
    console.log('🔍 showUploadModal 설정 완료');
  };

  const handleUploadClose = () => {
    setShowUploadModal(false);
  };

  const handleUploadSuccess = () => {
    // 업로드 성공 후 관련 데이터 새로고침
    queryClient.invalidateQueries(['contracts']);
    queryClient.invalidateQueries(['units']);
    queryClient.invalidateQueries(['tenants']);
    queryClient.invalidateQueries(['vacant-units']);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedContract(null);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries('contracts');
    handleCloseModal();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">오류가 발생했습니다: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 제목 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">계약 관리 (RMC 전용)</h1>
          <p className="mt-1 text-sm text-gray-500">
            관리하는 건물의 계약 현황 및 관리 - RMC 전용 화면
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleUploadClick}
            className="inline-flex items-center whitespace-nowrap px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Upload className="mr-2 h-4 w-4 flex-shrink-0" />
            엑셀 업로드
          </button>
          <button
            onClick={handleAddContract}
            className="inline-flex items-center whitespace-nowrap px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="mr-2 h-4 w-4 flex-shrink-0" />
            계약 추가
          </button>
        </div>
      </div>

      {/* 필터 섹션 */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="space-y-4">
          {/* 첫 번째 행: 상태 필터 */}
          <div className="flex items-center space-x-2">
            <FileText className="h-4 w-4 text-gray-400" />
            <label className="text-sm font-medium text-gray-700">상태:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">전체</option>
              <option value="active">활성</option>
              <option value="expired">만료</option>
              <option value="terminated">해지</option>
            </select>
          </div>

          {/* 두 번째 행: 임대사업자와 건물 필터 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 임대사업자 필터 (RMC 사용자용) */}
            {isRMCUser && (
              <div className="flex items-center space-x-2">
                <Home className="h-4 w-4 text-gray-400" />
                <label className="text-sm font-medium text-gray-700">임대사업자:</label>
                <select
                  value={selectedPropertyOwner}
                  onChange={(e) => handlePropertyOwnerChange(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">전체 임대사업자</option>
                  {propertyOwners && propertyOwners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.company_name || owner.name} ({owner.buildings_count}개 건물)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 건물 필터 (모든 사용자용) */}
            <div className="flex items-center space-x-2">
              <Home className="h-4 w-4 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">건물:</label>
              <select
                value={selectedBuilding}
                onChange={(e) => setSelectedBuilding(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={userInfo?.role === 'rental_management_company' && !selectedPropertyOwner}
              >
                <option value="">전체 건물</option>
                {buildings && buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* 계약 목록 */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            계약 목록 ({contracts.length}건)
          </h3>
        </div>
        
        {contracts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">계약이 없습니다.</div>
            <div className="text-gray-400 text-sm">
              새로운 계약을 추가해보세요.
            </div>
          </div>
        ) : (
          <>
          <div className="divide-y divide-gray-200 md:hidden">
            {contracts.map((contract) => (
              <div key={contract.id} className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {contract.contract_number || '-'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {contract.tenant?.name || '-'}
                    </p>
                  </div>
                  <span className={`inline-flex shrink-0 rounded-full px-2 py-1 text-xs font-semibold ${
                    contract.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : contract.status === 'expired'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {contract.status === 'active' ? '활성' :
                     contract.status === 'expired' ? '만료' : '대기'}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">건물/세대</span>
                    <span className="text-right font-medium text-gray-900">
                      {contract.unit?.building_name || '-'} / {contract.unit?.unit_number || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">계약기간</span>
                    <span className="text-right font-medium text-gray-900">
                      {contract.start_date} ~ {contract.end_date}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">월세</span>
                    <span className="text-right font-medium text-gray-900">
                      {contract.monthly_rent ? (contract.monthly_rent / 10000).toLocaleString() : 0}만원
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <button
                    onClick={() => handleViewContract(contract)}
                    className="inline-flex min-h-[40px] items-center justify-center gap-1 rounded-md border border-blue-200 px-2 text-sm font-medium text-blue-700"
                  >
                    <Eye className="h-4 w-4" />
                    보기
                  </button>
                  <button
                    onClick={() => handleEditContract(contract)}
                    className="inline-flex min-h-[40px] items-center justify-center gap-1 rounded-md border border-indigo-200 px-2 text-sm font-medium text-indigo-700"
                  >
                    <Edit className="h-4 w-4" />
                    수정
                  </button>
                  <button
                    onClick={() => handleDeleteContract(contract.id)}
                    className="inline-flex min-h-[40px] items-center justify-center gap-1 rounded-md border border-red-200 px-2 text-sm font-medium text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    계약번호
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    임차인
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    건물/세대
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    계약기간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    월세
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {contract.contract_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{contract.tenant?.name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contract.unit?.building_name || '-'} / {contract.unit?.unit_number || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contract.start_date} ~ {contract.end_date}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contract.monthly_rent ? (contract.monthly_rent / 10000).toLocaleString() : 0}만원
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        contract.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : contract.status === 'expired'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {contract.status === 'active' ? '활성' : 
                         contract.status === 'expired' ? '만료' : '해지'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewContract(contract)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditContract(contract)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteContract(contract.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* 계약 모달 */}
      {showModal && (
        <ContractModal
          isOpen={showModal}
          contract={selectedContract}
          mode={modalMode}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
        />
      )}

      {/* 업로드 모달 */}
      {showUploadModal && (
        <ContractUpload
          onClose={handleUploadClose}
          onSuccess={handleUploadSuccess}
          selectedBuilding={selectedBuilding ? buildings.find(b => String(b.id) === String(selectedBuilding)) : null}
        />
      )}
    </div>
  );
};

export default RMCContracts;
